import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2, 
  Hotel, 
  Link2, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft,
  Loader2,
  FileText,
  Info
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Portal-specific axios instance (avoids global interceptors)
const portalApi = axios.create({ baseURL: API_URL });

function parseApiError(err) {
  const data = err.response?.data;
  if (!data) return err.message || 'Network error';
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || String(d)).join(', ');
  return err.message || 'Request failed';
}

const STEPS = [
  { id: 'company', title: 'Company Details', icon: Building2, description: 'Your business information for e-invoices' },
  { id: 'property', title: 'Property Setup', icon: Hotel, description: 'Add your hotel/property' },
  { id: 'pms', title: 'Connect PMS', icon: Link2, description: 'Link your property management system' },
  { id: 'complete', title: 'Complete', icon: CheckCircle2, description: 'Ready to start' }
];

export default function PortalOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [states, setStates] = useState([]);
  const [msicCodes, setMsicCodes] = useState([]);
  const [msicSearch, setMsicSearch] = useState('');
  
  // Form data
  const [companyData, setCompanyData] = useState({
    business_name: '',
    trade_name: '',
    tin: '',
    brn: '',
    sst_number: '',
    ttx_number: '',
    msic_code: '55101',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    email: '',
    contact_person_name: '',
    contact_person_email: '',
    contact_person_phone: '',
    contact_person_position: '',
    bank_name: '',
    bank_account_number: '',
    commencement_date: ''
  });

  const [propertyData, setPropertyData] = useState({
    property_name: '',
    property_code: '',
    auto_create_invoice: true
  });

  const [pmsData, setPmsData] = useState({
    pms_type: '',
    read_token: '',
    write_token: ''
  });

  const token = localStorage.getItem('einvoice_token');

  useEffect(() => {
    if (!token) {
      navigate('/einvoice/portal/auth');
      return;
    }
    const loadData = async () => {
      setInitialLoading(true);
      await loadReferenceData();
      await loadOnboardingStatus();
      setInitialLoading(false);
    };
    loadData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [statesRes, msicRes] = await Promise.allSettled([
        portalApi.get('/api/einvoice/portal/reference/states'),
        portalApi.get('/api/einvoice/portal/reference/msic-codes')
      ]);
      
      if (statesRes.status === 'fulfilled') {
        setStates(statesRes.value.data.states || []);
      }
      if (msicRes.status === 'fulfilled') {
        setMsicCodes(msicRes.value.data.msic_codes || []);
      }
    } catch (err) {
      console.error('Failed to load reference data:', err);
    }
    
    // Set defaults if data wasn't loaded
    setStates(prev => prev.length > 0 ? prev : [
      { code: "01", name: "Johor" }, { code: "02", name: "Kedah" },
      { code: "03", name: "Kelantan" }, { code: "04", name: "Melaka" },
      { code: "05", name: "Negeri Sembilan" }, { code: "06", name: "Pahang" },
      { code: "07", name: "Pulau Pinang" }, { code: "08", name: "Perak" },
      { code: "09", name: "Perlis" }, { code: "10", name: "Selangor" },
      { code: "11", name: "Terengganu" }, { code: "12", name: "Sabah" },
      { code: "13", name: "Sarawak" }, { code: "14", name: "Kuala Lumpur" },
      { code: "15", name: "Labuan" }, { code: "16", name: "Putrajaya" }
    ]);
    setMsicCodes(prev => prev.length > 0 ? prev : [
      { code: "55101", description: "Hotels and similar accommodation" },
      { code: "55102", description: "Motels" },
      { code: "55103", description: "Resort hotels" },
      { code: "55104", description: "Suite/apartment hotels" },
      { code: "55901", description: "Hostels" },
      { code: "55902", description: "Chalets" },
      { code: "55903", description: "Guest houses" }
    ]);
  };

  const loadOnboardingStatus = async () => {
    try {
      const { data } = await portalApi.get('/api/einvoice/portal/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (data.company) {
        setCompanyData(prev => ({ ...prev, ...data.company }));
      }
      if (data.email) {
        setCompanyData(prev => ({ ...prev, email: prev.email || data.email }));
      }
      
      const progress = data.onboarding;
      if (progress) {
        const completed = progress.steps_completed || [];
        if (completed.includes('company_details') && !completed.includes('property_setup')) {
          setCurrentStep(1);
        } else if (completed.includes('property_setup') && !completed.includes('pms_connection')) {
          setCurrentStep(2);
        }
      }
    } catch (err) {
      console.error('Failed to load onboarding status:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('einvoice_token');
        navigate('/einvoice/portal/auth');
        return;
      }
      setError('Failed to load your profile. Please try refreshing the page.');
    }
  };

  const handleCompanyChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePropertyChange = (field, value) => {
    setPropertyData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePmsChange = (field, value) => {
    setPmsData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const isCompanyValid = () => {
    const required = ['business_name', 'tin', 'brn', 'msic_code', 'address_line1', 'city', 'state', 'postal_code', 'phone', 'email', 'commencement_date'];
    return required.every(field => companyData[field] && companyData[field].toString().trim() !== '');
  };

  const saveCompany = async () => {
    if (!isCompanyValid()) {
      setError('Please fill in all required fields before continuing.');
      return;
    }
    setLoading(true);
    setError('');
    
    try {
      await portalApi.post('/api/einvoice/portal/company', companyData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCurrentStep(1);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const saveProperty = async () => {
    setLoading(true);
    setError('');
    
    try {
      await portalApi.post('/api/einvoice/portal/properties', propertyData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCurrentStep(2);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const savePms = async () => {
    setLoading(true);
    setError('');
    
    try {
      await portalApi.post('/api/einvoice/portal/pms/connect', pmsData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCurrentStep(3);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const skipPms = () => {
    setPmsData({ pms_type: 'manual', read_token: '', write_token: '' });
    setCurrentStep(3);
  };

  const finishOnboarding = () => {
    navigate('/einvoice/portal/dashboard');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderCompanyForm();
      case 1:
        return renderPropertyForm();
      case 2:
        return renderPmsForm();
      case 3:
        return renderComplete();
      default:
        return null;
    }
  };

  const renderCompanyForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-[#4169E1] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Required for LHDN e-Invoice</p>
            <p>These details will appear on your e-invoices and must match your business registration.</p>
          </div>
        </div>
      </div>

      {/* Business Identity */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Business Identity</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Business Name *</Label>
            <Input
              value={companyData.business_name}
              onChange={(e) => handleCompanyChange('business_name', e.target.value)}
              placeholder="Your Company Sdn Bhd"
              data-testid="company-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Trade Name (Optional)</Label>
            <Input
              value={companyData.trade_name}
              onChange={(e) => handleCompanyChange('trade_name', e.target.value)}
              placeholder="Trading as..."
            />
          </div>
        </div>
      </div>

      {/* Tax Registration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Tax Registration</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Tax Identification Number (TIN) *</Label>
            <Input
              value={companyData.tin}
              onChange={(e) => handleCompanyChange('tin', e.target.value.toUpperCase())}
              placeholder="C1234567890"
              data-testid="company-tin-input"
            />
            <p className="text-xs text-slate-500 dark:text-gray-400">Format: C followed by 10-12 digits</p>
          </div>
          <div className="space-y-2">
            <Label>Business Registration No. (BRN) *</Label>
            <Input
              value={companyData.brn}
              onChange={(e) => handleCompanyChange('brn', e.target.value)}
              placeholder="202001234567"
              data-testid="company-brn-input"
            />
          </div>
          <div className="space-y-2">
            <Label>SST Registration No.</Label>
            <Input
              value={companyData.sst_number}
              onChange={(e) => handleCompanyChange('sst_number', e.target.value)}
              placeholder="Leave blank if not registered"
            />
          </div>
          <div className="space-y-2">
            <Label>Tourism Tax (TTX) Registration</Label>
            <Input
              value={companyData.ttx_number}
              onChange={(e) => handleCompanyChange('ttx_number', e.target.value)}
              placeholder="Leave blank if not applicable"
            />
          </div>
        </div>
      </div>

      {/* Industry Classification */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Industry Classification</h3>
        <div className="space-y-2">
          <Label>MSIC Code *</Label>
          <div className="relative">
            <Input
              value={msicSearch}
              onChange={(e) => setMsicSearch(e.target.value)}
              placeholder="Search by code or description..."
              className="mb-2"
              data-testid="msic-search-input"
            />
            <Select 
              value={companyData.msic_code} 
              onValueChange={(v) => handleCompanyChange('msic_code', v)}
            >
              <SelectTrigger data-testid="company-msic-select">
                <SelectValue placeholder="Select MSIC Code" />
              </SelectTrigger>
              <SelectContent>
                {msicCodes
                  .filter((msic) => {
                    if (!msicSearch) return true;
                    const q = msicSearch.toLowerCase();
                    return msic.code.includes(q) || msic.description.toLowerCase().includes(q);
                  })
                  .map((msic) => (
                    <SelectItem key={msic.code} value={msic.code}>
                      {msic.code} - {msic.description}
                    </SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tax Contact Person */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Person In Charge (Tax Matters)</h3>
        <p className="text-sm text-slate-500">Contact details of the person responsible for company tax affairs</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input
              value={companyData.contact_person_name}
              onChange={(e) => handleCompanyChange('contact_person_name', e.target.value)}
              placeholder="e.g. Ahmad bin Ali"
              data-testid="contact-person-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Position / Title</Label>
            <Input
              value={companyData.contact_person_position}
              onChange={(e) => handleCompanyChange('contact_person_position', e.target.value)}
              placeholder="e.g. Finance Manager"
              data-testid="contact-person-position-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={companyData.contact_person_email}
              onChange={(e) => handleCompanyChange('contact_person_email', e.target.value)}
              placeholder="tax@company.com"
              data-testid="contact-person-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input
              value={companyData.contact_person_phone}
              onChange={(e) => handleCompanyChange('contact_person_phone', e.target.value)}
              placeholder="+60123456789"
              data-testid="contact-person-phone-input"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Business Address</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Address Line 1 *</Label>
            <Input
              value={companyData.address_line1}
              onChange={(e) => handleCompanyChange('address_line1', e.target.value)}
              placeholder="Street address"
              data-testid="company-address-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Address Line 2</Label>
            <Input
              value={companyData.address_line2}
              onChange={(e) => handleCompanyChange('address_line2', e.target.value)}
              placeholder="Building, floor, etc."
            />
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City *</Label>
              <Input
                value={companyData.city}
                onChange={(e) => handleCompanyChange('city', e.target.value)}
                placeholder="City"
                data-testid="company-city-input"
              />
            </div>
            <div className="space-y-2">
              <Label>State *</Label>
              <Select 
                value={companyData.state} 
                onValueChange={(v) => handleCompanyChange('state', v)}
              >
                <SelectTrigger data-testid="company-state-select">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Postal Code *</Label>
              <Input
                value={companyData.postal_code}
                onChange={(e) => handleCompanyChange('postal_code', e.target.value)}
                placeholder="50000"
                maxLength={5}
                data-testid="company-postal-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Contact Information</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Phone *</Label>
            <Input
              value={companyData.phone}
              onChange={(e) => handleCompanyChange('phone', e.target.value)}
              placeholder="+60312345678"
              data-testid="company-phone-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={companyData.email}
              onChange={(e) => handleCompanyChange('email', e.target.value)}
              placeholder="accounts@company.com"
              data-testid="company-email-input"
            />
          </div>
        </div>
      </div>

      {/* Bank Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Bank Details (Optional)</h3>
        <p className="text-sm text-slate-500 dark:text-gray-400">For payment information on invoices</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Bank Name</Label>
            <Input
              value={companyData.bank_name}
              onChange={(e) => handleCompanyChange('bank_name', e.target.value)}
              placeholder="Maybank"
            />
          </div>
          <div className="space-y-2">
            <Label>Account Number</Label>
            <Input
              value={companyData.bank_account_number}
              onChange={(e) => handleCompanyChange('bank_account_number', e.target.value)}
              placeholder="1234567890"
            />
          </div>
        </div>
      </div>

      {/* Business Commencement Date */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Business Commencement Date <span className="text-red-500">*</span></h3>
        <p className="text-sm text-slate-500 dark:text-gray-400">The official date your business/hotel started operations. Only bookings with arrival dates on or after this date will be synced from Beds24.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Commencement Date <span className="text-red-500">*</span></Label>
            <Input
              type="date"
              value={companyData.commencement_date}
              onChange={(e) => handleCompanyChange('commencement_date', e.target.value)}
              data-testid="commencement-date-input"
            />
          </div>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-xs text-blue-700 dark:text-blue-300"><strong>Important:</strong> This date controls which historical bookings are imported from your PMS. Bookings before this date will not be synced. You can adjust the sync start date later in Settings &gt; LHDN API.</p>
        </div>
      </div>

      <Button
        onClick={saveCompany}
        disabled={loading || !isCompanyValid()}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="save-company-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Save & Continue
        <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );

  const renderPropertyForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Hotel className="w-5 h-5 text-[#4169E1] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Add Your Property</p>
            <p>Each property can have its own settings. You can add more properties later.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Property Name *</Label>
            <Input
              value={propertyData.property_name}
              onChange={(e) => handlePropertyChange('property_name', e.target.value)}
              placeholder="My Hotel"
              data-testid="property-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Property Code (3 letters)</Label>
            <Input
              value={propertyData.property_code}
              onChange={(e) => handlePropertyChange('property_code', e.target.value.toUpperCase())}
              placeholder="MYH"
              maxLength={3}
            />
            <p className="text-xs text-slate-500">Used internally to identify this property</p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="auto_invoice"
            checked={propertyData.auto_create_invoice}
            onChange={(e) => handlePropertyChange('auto_create_invoice', e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 dark:border-zinc-700"
          />
          <Label htmlFor="auto_invoice" className="text-sm font-normal">
            Automatically create invoices for new bookings
          </Label>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(0)}
          className="flex-1"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={saveProperty}
          disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
          data-testid="save-property-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save & Continue
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  const renderPmsForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex gap-3">
          <Link2 className="w-5 h-5 text-[#4169E1] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Connect Your PMS</p>
            <p>Link your property management system to automatically import bookings and create invoices.</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Label>Select Your PMS</Label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { id: 'beds24', name: 'Beds24', available: true },
            { id: 'cloudbeds', name: 'Cloudbeds', available: false },
            { id: 'siteminder', name: 'SiteMinder', available: false },
            { id: 'manual', name: 'Manual Entry', available: true }
          ].map((pms) => (
            <button
              key={pms.id}
              onClick={() => handlePmsChange('pms_type', pms.id)}
              disabled={!pms.available}
              className={`p-4 rounded-lg border-2 transition-all ${
                pmsData.pms_type === pms.id
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30'
                  : pms.available
                    ? 'border-slate-200 dark:border-zinc-800 hover:border-slate-300 dark:border-zinc-700'
                    : 'border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900 opacity-50 cursor-not-allowed'
              }`}
              data-testid={`pms-${pms.id}-btn`}
            >
              <div className="text-sm font-medium">{pms.name}</div>
              {!pms.available && (
                <div className="text-xs text-slate-400 dark:text-gray-500 mt-1">Coming Soon</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {pmsData.pms_type === 'beds24' && (
        <div className="space-y-4 p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg">
          <h4 className="font-medium">Beds24 API Tokens</h4>
          
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-slate-700 dark:text-gray-200 space-y-2">
            <p className="font-semibold text-blue-700">How to get your Beds24 API tokens:</p>
            <ol className="list-decimal list-inside space-y-1.5 text-slate-600 dark:text-gray-300">
              <li>Log in to your <a href="https://beds24.com" target="_blank" rel="noopener noreferrer" className="text-[#4169E1] underline font-medium">Beds24 account</a></li>
              <li>Go to <strong>Settings</strong> (gear icon) in the top menu</li>
              <li>Click <strong>Account</strong> → <strong>Account Access</strong></li>
              <li>Scroll down to the <strong>API</strong> section</li>
              <li>Copy the <strong>API Key (Read)</strong> — this is your Read Token</li>
              <li>Copy the <strong>API Key (Write)</strong> — this is your Write Token (optional)</li>
            </ol>
            <p className="text-xs text-slate-500 dark:text-gray-400 mt-2 pt-2 border-t border-blue-100">
              The Read Token allows Staymii to pull your booking data. The Write Token lets Staymii update invoice status in Beds24.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Read Token (Required)</Label>
              <Input
                value={pmsData.read_token}
                onChange={(e) => handlePmsChange('read_token', e.target.value)}
                placeholder="Paste your Beds24 API Key (Read) here"
                data-testid="beds24-read-token-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Write Token (Optional)</Label>
              <Input
                value={pmsData.write_token}
                onChange={(e) => handlePmsChange('write_token', e.target.value)}
                placeholder="Paste your Beds24 API Key (Write) here"
              />
            </div>
          </div>
        </div>
      )}

      {pmsData.pms_type === 'manual' && (
        <div className="p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg">
          <p className="text-sm text-slate-600 dark:text-gray-300">
            You'll enter bookings manually. You can always connect a PMS later from settings.
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(1)}
          className="flex-1"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {pmsData.pms_type ? (
          <Button
            onClick={savePms}
            disabled={loading || (pmsData.pms_type === 'beds24' && !pmsData.read_token)}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            data-testid="connect-pms-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {pmsData.pms_type === 'manual' ? 'Continue' : 'Connect'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={skipPms}
            className="flex-1"
          >
            Skip for Now
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-10 h-10 text-[#4169E1]" />
      </div>
      <div>
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white">You're All Set!</h3>
        <p className="text-slate-500 dark:text-gray-400 mt-2">
          Your e-Invoice portal is ready. Start creating invoices now.
        </p>
      </div>
      <div className="space-y-3">
        <Button
          onClick={finishOnboarding}
          className="w-full max-w-md bg-blue-600 hover:bg-blue-700"
          data-testid="finish-onboarding-btn"
        >
          Go to Dashboard
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4169E1] mx-auto mb-4" />
          <p className="text-slate-500 dark:text-gray-400">Loading your setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-slate-900 dark:text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 dark:text-white">Staymii e-Invoice</span>
          </div>
          <p className="text-slate-500 dark:text-gray-400">Complete your setup to start generating e-invoices</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div key={step.id} className="flex flex-col items-center flex-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all ${
                  isCompleted 
                    ? 'bg-blue-600 text-white'
                    : isActive 
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-[#4169E1] ring-2 ring-blue-600'
                      : 'bg-slate-100 dark:bg-zinc-800 text-slate-400 dark:text-gray-500'
                }`}>
                  {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs font-medium ${isActive ? 'text-[#4169E1]' : 'text-slate-500 dark:text-gray-400'}`}>
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
            <CardDescription>{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {renderStepContent()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
